import { SubscriptionStatusEnum, SubscriptionTierEnum } from "../../data-structures/enums";
import type { ITenantUser, MollieSubscription } from "../../data-structures/interfaces";

type SubscriptionCapacityShape = Pick<MollieSubscription, "method" | "tier" | "licenseCount" | "availableLicenseCount" | "status" | "expiryDateRaw">;

export const normalizeSubscriptionMethod = (method?: string): string => method?.trim().toLowerCase() ?? "";

export const isUnlimitedSubscription = (subscription?: Pick<MollieSubscription, "method" | "tier" | "licenseCount">): boolean => {
    if (!subscription) {
        return false;
    }

    const method = normalizeSubscriptionMethod(subscription.method);
    return subscription.tier === SubscriptionTierEnum.Trial
        || method === "invoice"
        || method === "invoice_dynamic"
        || subscription.licenseCount === null
        || subscription.licenseCount === undefined;
};

export const isSubscriptionAssignable = (subscription?: SubscriptionCapacityShape, now: Date = new Date()): boolean => {
    if (!subscription) {
        return false;
    }

    const status = (subscription.status as string)?.toLowerCase();
    const expiryDate = subscription.expiryDateRaw ? new Date(subscription.expiryDateRaw) : null;

    if (status === SubscriptionStatusEnum.Active) {
        return !expiryDate || expiryDate >= now;
    }

    if (status === SubscriptionStatusEnum.Canceled) {
        return !!expiryDate && expiryDate >= now;
    }

    return false;
};

export const getUserIdsRequiringSubscriptionAssignment = (
    userIds: number[],
    targetSubscriptionId: number,
    users: ITenantUser[]
): number[] => {
    return userIds.filter(userId => {
        const user = users.find(x => x.id === userId);
        return user?.subscriptionId !== targetSubscriptionId;
    });
};

export const canAssignUsersToSubscription = (
    subscription: SubscriptionCapacityShape | undefined,
    requiredLicenseCount: number,
    now: Date = new Date()
): boolean => {
    if (!subscription) {
        return false;
    }

    if (requiredLicenseCount <= 0) {
        return true;
    }

    if (!isSubscriptionAssignable(subscription, now)) {
        return false;
    }

    if (isUnlimitedSubscription(subscription)) {
        return true;
    }

    return typeof subscription.availableLicenseCount === "number"
        && subscription.availableLicenseCount >= requiredLicenseCount;
};
